// @flow
import React, {
  useState,
  useEffect,
  useRef,
  useReducer,
  useContext
} from "react";
import { get, withFetch } from "../common/fetch";
import { Modal } from "../common/Modal";
import Popup from "./Popup";
import { gaReducer } from "../GA";
import Api from "../Tournaments/Api";

const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

export const useServerTime = deps => {
  let [state, dispatch] = useReducer(
    (state, [type, payload]) => {
      switch (type) {
        case "receiveTime":
          return {
            time: payload,
            count: 0
          };
        case "tick":
          return {
            time: state.time,
            count: state.count + 1
          };
        default:
          return {
            time: Date.now(),
            count: 0
          };
      }
    },
    {
      time: Date.now(),
      count: 0
    }
  );

  useInterval(() => {
    dispatch(["tick"]);
  }, 1000);

  useEffect(() => {
    get("/api/server-time").then(data => {
      dispatch(["receiveTime", parseInt(data.time, 10)]);
    });
  }, deps);

  return state.time + state.count;
};

export const useCompensations = deps => {
  let [compensations, setCompensations] = useState(null);
  let api = useContext(Api);

  useEffect(() => {
    api
      .fetchEntity("/api/compensations/info")
      .then(data => setCompensations(data.value));
  }, deps);

  return compensations;
};

const chooseDealToCompensate = (
  deals,
  compensationsData,
  currentTime,
  onCompensation,
  accountType
) => {
  let compensations = compensationsData && compensationsData[accountType];
  if (
    deals.length === 0 ||
    compensations == null ||
    (compensations.items && compensations.items.length === 0)
  ) {
    return null;
  }
  for (let i = 0; i < deals.length; i++) {
    let currentDeal = deals[i];
    let compensateCountdown = currentDeal.can_compensate_countdown || 57;

    for (let compensation of compensations.items) {
      if (
        currentDeal.close_time > currentTime - compensateCountdown &&
        compensation.available > 0 &&
        compensation.amount_limit >= Math.abs(currentDeal.profit)
      ) {
        return currentDeal;
      } else if (currentDeal.close_time <= currentTime - compensateCountdown) {
        onCompensation(currentDeal);
      }
    }
  }

  return null;
};

type Props = {
  dealsToCompensate: Array<Deal>,
  onCompensation: deal => void,
  onCompensationCreated: () => void,
  currentTime: number,
  isDesktop: boolean
};

function sleep(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

const Protection = (props: Props) => {
  let [screen, setScreen] = useState("Compensate"); // "Compensate" | "Congratulations"
  let [info, setInfo] = useState(false);
  let [loading, setLoading] = useState(false);
  let compensations = useCompensations([
    props.dealsToCompensate,
    props.compensationUpdated,
    props.compensationCreated
  ]);

  const accountType = props.activeAccount.type;

  let currentTime = useServerTime([props.dealsToCompensate]);

  // если в сокет прилетело сообщение о создании компенсации,
  // то показываем попап о начислении компенсаций
  useEffect(() => {
    if (props.compensationCreated != null) {
      setInfo(false);
      setScreen("Refill");
    }
  }, [props.compensationCreated]);

  let deal = chooseDealToCompensate(
    props.dealsToCompensate,
    compensations,
    currentTime,
    props.onCompensation,
    accountType
  );

  const handleCompensation = () => {
    if (deal != null) {
      setLoading(true);
      props
        .fetch(`/api/compensate/?op_id=${deal.id}`)
        .then(() => {
          gaReducer("DEAL_COMPENSATED", { amount: Math.abs(deal.profit) });
          return sleep(1000);
        })
        .then(() => {
          setLoading(false);
          setScreen("Congratulations");
          props.onCompensation(deal);
        })
        .catch(err => {
          setLoading(false);
          props.onCompensation(deal);
          console.error("#", err);
        });
    }
  };

  const handleDismissCompensation = () => {
    props.onCompensation(deal);
  };

  const handleDismissCongratulations = () => {
    setScreen("Compensate");
  };

  const handleRefillCompensations = () => {
    setInfo(false);
    setScreen("Compensate");
  };

  const handleDetails = () => {
    setInfo(true);
    setScreen("Refill");
  };

  const compensateCountdown =
    (deal != null && deal.can_compensate_countdown) || 57;

  // Попап виден, если мы поздравляем клиента с успехом, или если ему есть чего
  // компенсировать.

  switch (screen) {
    case "Compensate":
      return (
        <Modal mode={deal != null}>
          <Popup
            deal={deal}
            time={
              deal != null &&
              compensateCountdown - (currentTime - deal.close_time)
            }
            onCompensation={handleCompensation}
            onDismiss={handleDismissCompensation}
            loading={loading}
            popupType="compensate"
            compensations={compensations}
            isDesktop={props.isDesktop}
            disableOutside={true}
            compensateCountdown={compensateCountdown}
            activeAccount={props.activeAccount}
          />
        </Modal>
      );
    case "Congratulations":
      return (
        <Modal mode={true}>
          <Popup
            onDismiss={handleDismissCongratulations}
            onCompensation={handleCompensation}
            onDetails={handleDetails}
            popupType="success"
            compensations={compensations}
            isDesktop={props.isDesktop}
            disableOutside={false}
            activeAccount={props.activeAccount}
          />
        </Modal>
      );
    case "Refill":
      return (
        <Modal mode={true}>
          <Popup
            onDismiss={handleRefillCompensations}
            onCompensation={handleCompensation}
            compensationCreated={props.compensationCreated}
            onCompensationCreated={props.onCompensationCreated}
            popupType="refill"
            compensations={compensations}
            isDesktop={props.isDesktop}
            disableOutside={false}
            isInfo={info}
            activeAccount={props.activeAccount}
          />
        </Modal>
      );
    default:
      return null;
  }
};

export default React.memo(withFetch(Protection));



// WEBPACK FOOTER //
// ./src/components/Protection/index.js